import apiClient from '../client';
import ENDPOINTS from '../endpoints';

/* A row from /roomParentAdmin/reminders/:networkId. The API calls the text
   `title`; the UI has always called it `text`, so the mapping happens here.
   `date` and `deadline` arrive as full ISO timestamps stored at UTC midnight —
   trim them to yyyy-mm-dd, which is what <input type="date"> and fmtDate both
   want. `time` is already "HH:mm" on a 24-hour clock. Any of them may be
   blank: a reminder can carry a day with no time, a deadline, a link, or none
   of those. */
function normalizeReminder(r) {
  return {
    id: r?._id || r?.id || '',
    text: r?.title || r?.text || '',
    date: r?.date ? String(r.date).slice(0, 10) : '',
    time: r?.time || '',
    /* an http(s) address the reminder points to, or '' */
    link: r?.link || '',
    /* by when parents need to act — not the same as `date` */
    deadline: r?.deadline ? String(r.deadline).slice(0, 10) : '',
    status: r?.status || 'active',
    /* the room parent who added it — the API returns the id only */
    createdBy: r?.createdBy || '',
  };
}

const remindersService = {
  /* Newest first, paginated per network; the API caps limit at 100. Defaults
     to active only, matching what the rest of the Links page shows. */
  list: async (networkId, { page = 1, limit = 100, search = '', status = 'active' } = {}) => {
    const res = await apiClient.get(ENDPOINTS.ROOM_PARENT_ADMIN.REMINDERS_BY_NETWORK(networkId), {
      params: { page, limit, search, ...(status ? { status } : {}) },
    });
    const data = res?.data ?? res;
    const rawList =
      data?.reminders || data?.docs || data?.items || data?.results || (Array.isArray(data) ? data : []);
    const total = Number(data?.totalCount ?? data?.total ?? data?.count ?? rawList.length);
    const totalPages = Number(data?.totalPages ?? Math.max(1, Math.ceil(total / limit)));
    return { reminders: rawList.map(normalizeReminder), total, totalPages };
  },

  /* { networkId, text, date?, time?, link?, deadline? } — a blank optional is
     left off rather than sent empty, so the row is stored with none. */
  create: ({ networkId, text, date, time, link, deadline }) =>
    apiClient.post(ENDPOINTS.ROOM_PARENT_ADMIN.REMINDER, {
      networkId,
      title: text,
      ...(date ? { date } : {}),
      ...(time ? { time } : {}),
      ...(link ? { link } : {}),
      ...(deadline ? { deadline } : {}),
    }),

  /* { id, ...only what changed }. Passing an optional explicitly as '' is how
     a value already saved gets cleared, so those keys are forwarded whenever
     the caller mentions them at all — including when blank. */
  update: ({ id, text, date, time, link, deadline, status }) =>
    apiClient.put(ENDPOINTS.ROOM_PARENT_ADMIN.REMINDER, {
      id,
      ...(text !== undefined ? { title: text } : {}),
      ...(date !== undefined ? { date: date || '' } : {}),
      ...(time !== undefined ? { time: time || '' } : {}),
      ...(link !== undefined ? { link: link || '' } : {}),
      ...(deadline !== undefined ? { deadline: deadline || '' } : {}),
      ...(status !== undefined ? { status } : {}),
    }),

  /* A real delete, unlike donation methods — the row is gone for good. */
  remove: (networkId, id) => apiClient.delete(ENDPOINTS.ROOM_PARENT_ADMIN.REMINDER_DELETE(networkId, id)),
};

export default remindersService;
